import { useCallback, useMemo, useRef, useState } from 'react';

const SWIPE_THRESHOLD = 50;
const TAP_THRESHOLD = 10;

const SwipeableCard = ({ children, onSwipeLeft, onClick }) => {
  const startXRef = useRef(null);
  const isTouchingRef = useRef(false);
  const [translateX, setTranslateX] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const resetCardPosition = useCallback(() => {
    setIsTransitioning(true);
    setTranslateX(0);
  }, []);

  const handleTouchStart = useCallback((event) => {
    if (event.touches.length !== 1) {
      return;
    }

    const { clientX } = event.touches[0];
    startXRef.current = clientX;
    isTouchingRef.current = true;
    setIsTransitioning(false);
  }, []);

  const handleTouchMove = useCallback((event) => {
    if (!isTouchingRef.current || startXRef.current == null) {
      return;
    }

    const { clientX } = event.touches[0];
    const deltaX = Math.min(clientX - startXRef.current, 0);

    setTranslateX(deltaX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isTouchingRef.current) {
      return;
    }

    isTouchingRef.current = false;

    const distance = translateX;
    const absoluteDistance = Math.abs(distance);

    if (absoluteDistance >= SWIPE_THRESHOLD) {
      setIsTransitioning(true);
      const offscreenX = -1 * (typeof window !== 'undefined' ? window.innerWidth : 300);
      setTranslateX(offscreenX);
      if (typeof onSwipeLeft === 'function') {
        onSwipeLeft();
      }
      startXRef.current = null;
      return;
    }

    if (absoluteDistance < TAP_THRESHOLD) {
      if (typeof onClick === 'function') {
        onClick();
      }
    }

    resetCardPosition();
    startXRef.current = null;
  }, [onClick, onSwipeLeft, resetCardPosition, translateX]);

  const handleTransitionEnd = useCallback(() => {
    if (translateX === 0) {
      setIsTransitioning(false);
    }
  }, [translateX]);

  const handleClick = useCallback(() => {
    if (isTouchingRef.current) {
      return;
    }

    if (typeof onClick === 'function') {
      onClick();
    }
  }, [onClick]);

  const wrapperStyle = useMemo(
    () => ({
      transform: `translateX(${translateX}px)`,
      transition: isTransitioning ? 'transform 200ms ease-out' : 'none',
      touchAction: 'pan-y',
    }),
    [isTransitioning, translateX],
  );

  return (
    <div
      style={wrapperStyle}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={resetCardPosition}
      onTransitionEnd={handleTransitionEnd}
    >
      {children}
    </div>
  );
};

export default SwipeableCard;


